import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import Input from '../Common/Input';
import Button from '../Common/Button';
import { ExperienceEntry } from '../../types/levelUp';
import './Experiences.css';

interface ExperiencesProps {
  enhancedCharacter: EnhancedCharacter;
  updateCharacterData: (updates: any) => void;
}

const Experiences: React.FC<ExperiencesProps> = ({ enhancedCharacter, updateCharacterData }) => {
  if (!enhancedCharacter.character) {
    return (
      <motion.div
        className="experiences"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="experiences__loading">
          Loading experiences...
        </div>
      </motion.div>
    );
  }

  const character = enhancedCharacter.character;
  const experiences = (character.experiences || []) as ExperienceEntry[];

  const handleExperienceNameChange = (index: number) => (value: string) => {
    const updatedExperiences = [...experiences];
    updatedExperiences[index] = {
      ...updatedExperiences[index],
      name: value,
    };
    updateCharacterData({ experiences: updatedExperiences });
  };

  const handleExperienceValueChange = (index: number) => (value: string) => {
    const updatedExperiences = [...experiences];
    const numValue = parseInt(value.replace(/\+/g, ''), 10) || 1;
    updatedExperiences[index] = {
      ...updatedExperiences[index],
      bonus: numValue,
    };
    updateCharacterData({ experiences: updatedExperiences });
  };

  const addExperience = () => {
    const newExp: ExperienceEntry = {
      id: `exp-${Date.now()}`,
      name: '',
      bonus: 2,
      createdAtLevel: parseInt(character.level) as any || 1
    };
    const updatedExperiences = [...experiences, newExp];
    updateCharacterData({ experiences: updatedExperiences });
  };

  const removeExperience = (index: number) => {
    const updatedExperiences = experiences.filter((_, i) => i !== index);
    updateCharacterData({ experiences: updatedExperiences });
  };


  return (
    <motion.div 
      className="experiences"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.1 }}
    >
      <motion.div 
        className="experiences__list"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, staggerChildren: 0.1 }}
      >
        <AnimatePresence>
          {experiences.map((experience, index) => (
            <motion.div
              key={experience.id} 
              className="experiences__experience"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.15 }}
              layout
              whileHover={{ scale: 1.01 }}
            >
              <div className="experiences__experience-row">
                <div className="experiences__name-section">
                  <Input
                    value={experience.name}
                    onChange={handleExperienceNameChange(index)}
                    placeholder={`Experience ${index + 1}`}
                    label={`Experience ${index + 1} Name`}
                    title={experience.name || `Experience ${index + 1}`}
                  />
                </div>
                <div className="experiences__value-section">
                  <Input
                    value={`+${experience.bonus}`}
                    onChange={handleExperienceValueChange(index)}
                    type="SIGNED_NUMBER"
                    placeholder="+2"
                    label="Bonus"
                  />
                </div>
                <div className="experiences__remove-section">
                  <motion.div
                    whileHover={{ scale: 1.05}}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="CLEAR"
                      size="SMALL"
                      onClick={() => removeExperience(index)}
                    >
                      Remove
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + experiences.length * 0.05, duration: 0.3 }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant="SECONDARY"
              size="SMALL"
              onClick={addExperience}
            >
              + Add Experience
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Experiences;